import React, { useState } from 'react';
import {
  useMediaStreamTrack,
  useDevices,
  useVideoContext,
  useUpBreakpoint,
} from '@uplift/hooks';
import {
  SELECTED_VIDEO_INPUT_KEY,
  SELECTED_AUDIO_INPUT_KEY,
  DEFAULT_VIDEO_CONSTRAINTS,
} from '@uplift/constants';
import { localStorage } from '@uplift/services';

import { LocalAudioTrack } from 'twilio-video';
import { MediaSetupDesktop } from './media-setup.desktop';
import { MediaSetupMobile, MediaSetupProps } from '.';
import { SelectOption } from '@uplift/ui-v2';
import { DisplayName } from '@uplift/utils';

export const MEDIA_DEFAULT_OPTION = {
  value: 'default',
  label: 'Default',
  disabled: true,
};

type Props = {
  blurAvailable?: boolean;
  name?: DisplayName;
  startTime?: string;
};

export const MediaSetupComponent: React.FC<Props> = ({ blurAvailable, name, startTime }) => {
  const isMdUp = useUpBreakpoint('md');
  const { localTracks, isVideoFilterSupported, toggleVideoFilter } = useVideoContext();

  const displayBlurToggle = blurAvailable && isVideoFilterSupported;

  const {
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
  } = useDevices();

  /// *** Audio Input **** ///
  const localAudioTrack = localTracks.find(track => track.kind === 'audio') as LocalAudioTrack;
  const localAudioMediaTrackStream = useMediaStreamTrack(localAudioTrack);
  const localAudioInputDeviceId = localAudioMediaTrackStream?.getSettings().deviceId;

  /// *** Video Output *** ///
  const localVideoTrack = localTracks.find(track => track.kind === 'video');

  const localVideoMediaTrackStream = useMediaStreamTrack(localVideoTrack);
  const [ storedLocalVideoDeviceId, setStoredLocalVideoDeviceId ] = useState(
    localStorage.getItem(SELECTED_VIDEO_INPUT_KEY) || 'default'
  );

  const localVideoInputDeviceId = localVideoMediaTrackStream?.getSettings().deviceId || storedLocalVideoDeviceId;
  ////

  const replaceAudioInputTrack = (newDeviceId: string) => {
    localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, newDeviceId);
    localAudioTrack?.restart({ deviceId: { exact: newDeviceId } });
  };

  const replaceLocalVideoTrack = (newDeviceId: string) => {
    // Here we store the device ID in the component state. This is so we can re-render this component display
    // to display the name of the selected device when it is changed while the users camera is off.
    setStoredLocalVideoDeviceId(newDeviceId);
    localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, newDeviceId);
    localVideoTrack?.restart({
      ...(DEFAULT_VIDEO_CONSTRAINTS as MediaTrackConstraints),
      deviceId: { exact: newDeviceId },
    });
    // Resets video filter state
    toggleVideoFilter(true);
  };

  const audioInputOptions: SelectOption[] =
    audioInputDevices.length === 0
      ? [ MEDIA_DEFAULT_OPTION ]
      : audioInputDevices.map((device) => ({
        value: device.deviceId,
        label: device.label,
      }));

  const audioOutputOptions: SelectOption[] =
    audioOutputDevices.length === 0
      ? [ MEDIA_DEFAULT_OPTION ]
      : audioOutputDevices.map((device) => ({
        value: device.deviceId,
        label: device.label,
      }));

  const videoInputOptions: SelectOption[] =
    videoInputDevices.length === 0
      ? [ MEDIA_DEFAULT_OPTION ]
      : videoInputDevices.map((device) => ({
        value: device.deviceId,
        label: device.label,
      }));

  const props: MediaSetupProps = {
    audioInputOptions,
    audioOutputOptions,
    videoInputOptions,
    localAudioInputDeviceId,
    localVideoInputDeviceId,
    displayBlurToggle,
    name,
    startTime,
    replaceAudioInputTrack,
    replaceLocalVideoTrack
  };

  return isMdUp ? (
    <MediaSetupDesktop {...props} />
  ) : (
    <MediaSetupMobile {...props} />
  );
};
